import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile'
import { turnstileSiteKey } from '../../consts/other'
import { VERIFY_CAPTCHA } from '../../redux/captchaReducer'
import { RootReducer } from '../../redux/rootReducer'
import track from '../../helpers/track'

const CaptchaPage = (): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isVerifying = useSelector((state: RootReducer) => state.captchaReducer.isVerifying)
  const verifyFailed = useSelector((state: RootReducer) => state.captchaReducer.verifyFailed)
  const turnstileRef = useRef<TurnstileInstance | null>(null)
  const submittingRef = useRef(false)
  const [widgetError, setWidgetError] = useState<string | null>(null)

  useEffect(() => {
    track('dealer_captcha_shown')
  }, [])

  const resetWidget = useCallback((): void => {
    turnstileRef.current?.reset()
  }, [])

  const handleToken = useCallback(
    (token: string): void => {
      if (submittingRef.current || isVerifying) {
        return
      }

      submittingRef.current = true
      setWidgetError(null)
      dispatch(VERIFY_CAPTCHA(token))
    },
    [dispatch, isVerifying]
  )

  useEffect(() => {
    if (!verifyFailed) {
      return
    }

    submittingRef.current = false
    resetWidget()
  }, [verifyFailed, resetWidget])

  const handleError = useCallback((): void => {
    if (isVerifying) {
      return
    }
    setWidgetError(t('captcha_page.error'))
  }, [isVerifying, t])

  const handleExpire = useCallback((): void => {
    if (isVerifying) {
      return
    }
    setWidgetError(t('captcha_page.expired'))
    resetWidget()
  }, [isVerifying, resetWidget, t])

  const handleScriptError = useCallback((): void => {
    setWidgetError(t('captcha_page.load_error'))
  }, [t])

  const errorMessage = verifyFailed ? t('captcha_page.error') : widgetError

  return (
    <div className="w-screen h-screen bg-gradient-to-b from-sky-700 to-sky-800 flex justify-center items-center px-4">
      <div className="w-full max-w-md bg-white rounded-4 shadow-dropBig px-8 py-10 flex flex-col items-center gap-6">
        <p className="text-center text-darkGrey text-xl font-semibold font-Montserrat leading-6">
          {t('captcha_page.title')}
        </p>
        <div className={isVerifying ? 'pointer-events-none opacity-60' : ''}>
          <Turnstile
            ref={turnstileRef}
            siteKey={turnstileSiteKey}
            options={{
              action: 'dealer_access',
              theme: 'light'
            }}
            onSuccess={handleToken}
            onError={handleError}
            onExpire={handleExpire}
            scriptOptions={{ onError: handleScriptError }}
          />
        </div>
        {errorMessage !== null && errorMessage !== '' && (
          <p className="text-center text-errorRed text-sm font-medium font-Montserrat">{errorMessage}</p>
        )}
        {isVerifying && (
          <div className="flex items-center gap-2 text-grey text-sm font-medium font-Montserrat">
            <div className="w-5 h-5 border-2 border-groupBlue/20 border-t-groupBlue rounded-full animate-spin" />
            <span>{t('captcha_page.verifying')}</span>
          </div>
        )}
      </div>
    </div>
  )
}

export default CaptchaPage
